import { useEffect, useRef, useState, type MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import { BrandLogo } from '../components/BrandLogo';
import { ScoreRing } from '../components/ScoreRing';
import heroImage from '../assets/landing-hero-park.png';
import heatCityImage from '../assets/landing-heat-city.png';
import communityImage from '../assets/landing-community.png';
import workshopImage from '../assets/landing-workshop.png';
import phonePreview from '../assets/landing-phone-preview.png';
import activePark from '../assets/active-park.webp';
import morningRun from '../assets/morning-run.webp';

const bentoFeatures = [
  {
    className: 'landing-bento-card landing-bento-card--map',
    kicker: 'Explore',
    title: 'Peta kenyamanan yang terasa hidup',
    text: 'Lihat area nyaman, kehijauan, fasilitas, dan alasan rekomendasi dalam satu peta interaktif semi-3D.',
    icon: '⌖',
  },
  {
    className: 'landing-bento-card landing-bento-card--score',
    kicker: 'Understand',
    title: 'Comfort Score dalam bahasa manusia',
    text: 'Bukan sekadar angka—setiap skor menjelaskan apa artinya bagi aktivitas warga.',
    icon: '85',
  },
  {
    className: 'landing-bento-card landing-bento-card--activity',
    kicker: 'Move',
    title: 'Aktivitas & Strava',
    text: 'Hubungkan rute dengan suhu, udara, dan karakter lingkungan yang dilalui.',
    icon: '↗',
  },
  {
    className: 'landing-bento-card landing-bento-card--learn',
    kicker: 'Learn',
    title: 'Edukasi iklim kontekstual',
    text: 'Materi muncul sesuai kondisi nyata yang sedang dilihat pengguna.',
    icon: '◫',
  },
  {
    className: 'landing-bento-card landing-bento-card--action',
    kicker: 'Act',
    title: 'Dari insight menuju aksi warga',
    text: 'Temukan kegiatan, tantangan mingguan, dan gerakan komunitas di sekitar.',
    icon: '✦',
  },
];

const journeySteps = [
  {
    label: '01 · Lihat',
    title: 'Mulai dari kondisi kota yang sedang terjadi.',
    text: 'Cuaca, kualitas udara, panas, kehijauan, dan fasilitas disatukan menjadi gambaran yang cepat dipahami.',
    image: heroImage,
    meta: 'Live context · city overview',
  },
  {
    label: '02 · Pahami',
    title: 'Ubah data teknis menjadi cerita yang relevan.',
    text: 'JakComfort menjelaskan mengapa suatu area terasa panas, teduh, atau kurang ideal untuk aktivitas tertentu.',
    image: heatCityImage,
    meta: 'Climate literacy · simple language',
  },
  {
    label: '03 · Putuskan',
    title: 'Pilih waktu, tempat, dan rute yang lebih baik.',
    text: 'Rekomendasi disesuaikan untuk jalan santai, lari, sepeda, wisata, atau aktivitas keluarga.',
    image: morningRun,
    meta: 'Personal recommendation · active city',
  },
  {
    label: '04 · Bertindak',
    title: 'Ajak warga bergerak bersama menjaga kota.',
    text: 'Edukasi terhubung dengan clean run, penghijauan, kelas publik, dan aksi komunitas yang dapat diikuti.',
    image: communityImage,
    meta: 'Citizen action · collective impact',
  },
];

const marqueeItems = [
  'Comfort Score',
  'Peta semi-3D',
  'Kualitas udara',
  'Ruang hijau',
  'Analisis aktivitas',
  'Edukasi iklim',
  'Aksi komunitas',
  'Story Maker',
];

export function LandingPage() {
  const [activeJourney, setActiveJourney] = useState(0);
  const heroVisualRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const revealElements = Array.from(document.querySelectorAll<HTMLElement>('[data-reveal]'));
    const revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            revealObserver.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' },
    );
    revealElements.forEach((element) => revealObserver.observe(element));

    const stepElements = Array.from(document.querySelectorAll<HTMLElement>('[data-journey-step]'));
    const stepObserver = new IntersectionObserver(
      (entries) => {
        const visibleEntry = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (!visibleEntry) return;
        const index = Number((visibleEntry.target as HTMLElement).dataset.journeyStep ?? 0);
        setActiveJourney(index);
      },
      { threshold: [0.25, 0.45, 0.65], rootMargin: '-25% 0px -45% 0px' },
    );
    stepElements.forEach((element) => stepObserver.observe(element));

    const updateScroll = () => {
      const maxScroll = Math.max(document.documentElement.scrollHeight - window.innerHeight, 1);
      const progress = Math.min(window.scrollY / maxScroll, 1);
      document.documentElement.style.setProperty('--landing-scroll-progress', progress.toString());
      document.documentElement.style.setProperty('--landing-hero-shift', `${Math.min(window.scrollY * 0.08, 52)}px`);
    };
    updateScroll();
    window.addEventListener('scroll', updateScroll, { passive: true });

    return () => {
      revealObserver.disconnect();
      stepObserver.disconnect();
      window.removeEventListener('scroll', updateScroll);
    };
  }, []);

  function handleHeroPointer(event: MouseEvent<HTMLDivElement>) {
    const element = heroVisualRef.current;
    if (!element) return;
    const rect = element.getBoundingClientRect();
    element.style.setProperty('--spotlight-x', `${event.clientX - rect.left}px`);
    element.style.setProperty('--spotlight-y', `${event.clientY - rect.top}px`);
    element.style.setProperty('--tilt-x', `${((event.clientY - rect.top) / rect.height - 0.5) * -3.5}deg`);
    element.style.setProperty('--tilt-y', `${((event.clientX - rect.left) / rect.width - 0.5) * 4.5}deg`);
  }

  function resetHeroPointer() {
    const element = heroVisualRef.current;
    if (!element) return;
    element.style.setProperty('--tilt-x', '0deg');
    element.style.setProperty('--tilt-y', '0deg');
  }

  return (
    <div className="landing-page landing-wow">
      <div className="landing-scroll-progress" aria-hidden="true" />

      <header className="landing-header landing-wow__header container">
        <Link to="/" className="landing-header__brand" aria-label="JakComfort home">
          <BrandLogo />
        </Link>
        <nav className="landing-header__nav" aria-label="Navigasi landing page">
          <a href="#fitur">Fitur</a>
          <a href="#pengalaman">Cara kerja</a>
          <a href="#edukasi">Edukasi</a>
          <a href="#aksi">Aksi warga</a>
        </nav>
        <Link className="button button--primary button--small landing-shimmer-button" to="/app">
          Buka JakComfort <span aria-hidden="true">↗</span>
        </Link>
      </header>

      <main>
        <section className="landing-hero container">
          <div className="landing-hero__ambient" aria-hidden="true">
            <span className="landing-orb landing-orb--one" />
            <span className="landing-orb landing-orb--two" />
            <span className="landing-orb landing-orb--three" />
            <span className="landing-grid-glow" />
          </div>

          <div className="landing-hero__content" data-reveal>
            <div className="landing-announcement">
              <span className="landing-announcement__dot" />
              Prototype literasi iklim & aksi warga Jakarta
              <span aria-hidden="true">→</span>
            </div>
            <h1>
              Jakarta lebih nyaman dimulai dari
              <span> keputusan yang lebih cerdas.</span>
            </h1>
            <p>
              JakComfort menerjemahkan data cuaca, udara, panas, kehijauan, dan fasilitas kota menjadi rekomendasi sederhana—agar warga dapat memahami, memilih, dan bertindak.
            </p>
            <div className="landing-hero__actions">
              <Link className="button button--primary landing-primary-cta landing-shimmer-button" to="/app">
                Jelajahi JakComfort <span aria-hidden="true">→</span>
              </Link>
              <a className="landing-video-link" href="#pengalaman">
                <span className="landing-video-link__icon" aria-hidden="true">▶</span>
                <span><strong>Lihat cara kerja</strong><small>Scroll untuk mengikuti perjalanan</small></span>
              </a>
            </div>
            <div className="landing-proof-row">
              <div><strong>Open data</strong><span>ringan & transparan</span></div>
              <div><strong>Mobile-first</strong><span>untuk masyarakat umum</span></div>
              <div><strong>Climate action</strong><span>dari insight ke aksi</span></div>
            </div>
          </div>

          <div
            className="landing-hero__visual"
            ref={heroVisualRef}
            onMouseMove={handleHeroPointer}
            onMouseLeave={resetHeroPointer}
            data-reveal
            data-reveal-delay="2"
          >
            <div className="landing-hero__spotlight" aria-hidden="true" />
            <div className="landing-browser-frame">
              <div className="landing-browser-frame__bar">
                <div><i /><i /><i /></div>
                <span>jakcomfort.id/app</span>
                <small>Live context</small>
              </div>
              <div className="landing-browser-frame__image">
                <img src={heroImage} alt="Ruang hijau perkotaan Jakarta dengan warga beraktivitas" />
                <div className="landing-browser-frame__shade" />
                <div className="landing-map-path" aria-hidden="true">
                  <span className="landing-map-path__line" />
                  <i className="landing-map-pin landing-map-pin--one">82</i>
                  <i className="landing-map-pin landing-map-pin--two">76</i>
                  <i className="landing-map-pin landing-map-pin--three">88</i>
                </div>
              </div>
            </div>

            <div className="landing-floating-card landing-floating-card--score">
              <ScoreRing score={85} label="Nyaman" size="sm" />
              <div><small>Comfort Score</small><strong>Pagi terbaik minggu ini</strong><span>06.00–09.00</span></div>
            </div>

            <div className="landing-floating-card landing-floating-card--air">
              <span className="landing-floating-card__icon">≋</span>
              <div><small>Kualitas udara</small><strong>Baik untuk aktivitas</strong><span>AQI 54 · PM2.5 rendah</span></div>
            </div>

            <div className="landing-phone-card">
              <img src={phonePreview} alt="Tampilan aplikasi JakComfort di ponsel" />
              <span className="landing-phone-card__shine" aria-hidden="true" />
            </div>
          </div>
        </section>

        <section className="landing-marquee" aria-label="Fitur utama JakComfort">
          <div className="landing-marquee__track">
            {[...marqueeItems, ...marqueeItems].map((item, index) => (
              <span key={`${item}-${index}`}><i>✦</i>{item}</span>
            ))}
          </div>
        </section>

        <section className="landing-section container" id="fitur">
          <div className="landing-section-heading landing-section-heading--split" data-reveal>
            <div>
              <span className="landing-kicker">Satu pengalaman, banyak manfaat</span>
              <h2>Bukan sekadar dashboard.<br />Ini cara baru memahami kota.</h2>
            </div>
            <p>
              Setiap blok memiliki fokus visual, respons interaktif, dan hierarki informasi yang jelas tanpa membuat pengguna kewalahan.
            </p>
          </div>

          <div className="landing-bento-grid">
            {bentoFeatures.map((feature, index) => (
              <article
                className={feature.className}
                key={feature.title}
                data-reveal
                data-reveal-delay={String((index % 3) + 1)}
              >
                <div className="landing-bento-card__glow" aria-hidden="true" />
                <div className="landing-bento-card__top">
                  <span className="landing-bento-card__kicker">{feature.kicker}</span>
                  <span className="landing-bento-card__icon">{feature.icon}</span>
                </div>
                <div className="landing-bento-card__content">
                  <h3>{feature.title}</h3>
                  <p>{feature.text}</p>
                </div>
                {index === 0 && (
                  <div className="landing-mini-map" aria-hidden="true">
                    <span className="landing-mini-map__road landing-mini-map__road--one" />
                    <span className="landing-mini-map__road landing-mini-map__road--two" />
                    <i>88</i><i>76</i><i>82</i>
                  </div>
                )}
                {index === 1 && (
                  <div className="landing-score-wave" aria-hidden="true">
                    <span /><span /><span /><span /><span /><span /><span />
                  </div>
                )}
                {index === 2 && <img className="landing-bento-card__photo" src={morningRun} alt="Aktivitas lari pagi di Jakarta" />}
                {index === 3 && <img className="landing-bento-card__photo" src={workshopImage} alt="Edukasi iklim bersama warga" />}
                {index === 4 && <img className="landing-bento-card__photo" src={communityImage} alt="Aksi lingkungan bersama komunitas" />}
              </article>
            ))}
          </div>
        </section>

        <section className="landing-journey" id="pengalaman">
          <div className="container landing-journey__grid">
            <div className="landing-journey__visual" data-reveal>
              <div className="landing-journey__sticky">
                <div className="landing-journey__halo" aria-hidden="true" />
                <div className="landing-journey__frame">
                  {journeySteps.map((step, index) => (
                    <img
                      key={step.title}
                      className={activeJourney === index ? 'is-active' : ''}
                      src={step.image}
                      alt={step.title}
                    />
                  ))}
                  <div className="landing-journey__overlay" />
                  <div className="landing-journey__status">
                    <small>{journeySteps[activeJourney].meta}</small>
                    <strong>{journeySteps[activeJourney].title}</strong>
                    <div className="landing-journey__dots">
                      {journeySteps.map((step, index) => (
                        <span key={step.label} className={activeJourney === index ? 'is-active' : ''} />
                      ))}
                    </div>
                  </div>
                </div>
                <div className="landing-journey__metric landing-journey__metric--left">
                  <strong>{activeJourney === 0 ? '85' : activeJourney === 1 ? '34°C' : activeJourney === 2 ? '06:30' : '12 aksi'}</strong>
                  <span>{activeJourney === 0 ? 'Comfort Score' : activeJourney === 1 ? 'Suhu terasa' : activeJourney === 2 ? 'Waktu ideal' : 'Komunitas aktif'}</span>
                </div>
                <div className="landing-journey__metric landing-journey__metric--right">
                  <strong>{activeJourney === 0 ? '74%' : activeJourney === 1 ? 'AQI 54' : activeJourney === 2 ? '62%' : '450+'}</strong>
                  <span>{activeJourney === 0 ? 'Kehijauan' : activeJourney === 1 ? 'Kualitas udara' : activeJourney === 2 ? 'Rute hijau' : 'Partisipan'}</span>
                </div>
              </div>
            </div>

            <div className="landing-journey__content">
              <div className="landing-section-heading" data-reveal>
                <span className="landing-kicker">Scroll-driven experience</span>
                <h2>Dari melihat kondisi hingga ikut mengubah kota.</h2>
                <p>Setiap tahap tersambung, sehingga data tidak berhenti menjadi angka.</p>
              </div>

              <div className="landing-journey__steps">
                {journeySteps.map((step, index) => (
                  <article
                    key={step.title}
                    className={activeJourney === index ? 'is-active' : ''}
                    data-journey-step={index}
                  >
                    <span>{step.label}</span>
                    <h3>{step.title}</h3>
                    <p>{step.text}</p>
                    <Link to={index === 3 ? '/app/actions' : index === 1 ? '/app/learn' : '/app'}>
                      Jelajahi tahap ini <b aria-hidden="true">↗</b>
                    </Link>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="landing-section landing-climate-section" id="edukasi">
          <div className="container">
            <div className="landing-section-heading landing-section-heading--center" data-reveal>
              <span className="landing-kicker">Climate story, not climate jargon</span>
              <h2>Isu iklim terasa dekat ketika muncul di tempat dan waktu yang tepat.</h2>
              <p>JakComfort menghubungkan kondisi lokal, penjelasan sederhana, dan tindakan yang bisa dilakukan hari itu juga.</p>
            </div>

            <div className="landing-climate-story">
              <article className="landing-climate-story__main" data-reveal>
                <img src={heatCityImage} alt="Warga Jakarta menghadapi panas perkotaan" />
                <div className="landing-climate-story__overlay" />
                <div className="landing-climate-story__copy">
                  <span>Urban heat insight</span>
                  <h3>Mengapa area ini terasa lebih panas?</h3>
                  <p>Bangunan padat, permukaan keras, dan minim vegetasi dapat menyimpan panas lebih lama.</p>
                  <Link to="/app/learn">Pelajari dalam 3 menit →</Link>
                </div>
              </article>

              <article className="landing-climate-story__side" data-reveal data-reveal-delay="2">
                <img src={activePark} alt="Aktivitas warga di ruang hijau" />
                <div>
                  <span>Green relief</span>
                  <h3>Ruang hijau bukan dekorasi—ia infrastruktur kenyamanan.</h3>
                  <p>Teduh, interaksi sosial, aktivitas fisik, dan pembelajaran lingkungan bertemu di satu tempat.</p>
                </div>
              </article>

              <article className="landing-climate-story__side landing-climate-story__side--orange" data-reveal data-reveal-delay="3">
                <img src={workshopImage} alt="Workshop literasi iklim bersama warga" />
                <div>
                  <span>Learn together</span>
                  <h3>Edukasi yang tidak menggurui.</h3>
                  <p>Konten singkat, kuis, dan tindakan harian membantu warga belajar melalui pengalaman.</p>
                </div>
              </article>
            </div>
          </div>
        </section>

        <section className="landing-community-stage" id="aksi">
          <div className="landing-community-stage__media" data-reveal>
            <img src={communityImage} alt="Komunitas JakComfort melakukan aksi penghijauan" />
            <div className="landing-community-stage__shade" />
          </div>
          <div className="container landing-community-stage__content">
            <div className="landing-community-stage__copy" data-reveal>
              <span className="landing-kicker landing-kicker--light">Citizen climate action</span>
              <h2>Teknologi hanya berarti jika membantu warga bergerak bersama.</h2>
              <p>
                JakComfort membuka ruang co-design, kurasi lokasi, edukasi publik, clean run, penghijauan, dan kegiatan komunitas yang dapat diikuti masyarakat.
              </p>
              <div className="landing-community-stage__actions">
                <Link className="button button--light" to="/app/actions">Temukan aksi terdekat</Link>
                <Link className="landing-community-stage__link" to="/app/story">Bagikan lewat Story Maker →</Link>
              </div>
            </div>
            <div className="landing-community-stage__stats" data-reveal data-reveal-delay="2">
              <article><strong>12+</strong><span>format kegiatan komunitas</span></article>
              <article><strong>5</strong><span>tahap dari data menuju aksi</span></article>
              <article><strong>0</strong><span>akun wajib untuk MVP</span></article>
            </div>
          </div>
        </section>

        <section className="landing-final-cta container" data-reveal>
          <div className="landing-final-cta__glow" aria-hidden="true" />
          <div>
            <span className="landing-kicker landing-kicker--light">Comfort in Every Move</span>
            <h2>Kenali kotamu.<br />Pilih langkahmu.<br /><span>Jaga Jakarta.</span></h2>
            <p>Masuk ke aplikasi dan lihat bagaimana data lingkungan dapat menjadi keputusan yang lebih sehat dan aksi yang lebih bermakna.</p>
          </div>
          <Link className="button button--light landing-final-cta__button" to="/app">
            Buka pengalaman JakComfort <span aria-hidden="true">↗</span>
          </Link>
        </section>
      </main>

      <footer className="landing-footer container landing-wow__footer">
        <BrandLogo />
        <p>Prototype MVP Jakarta Innovation Award 2026. Informasi kenyamanan merupakan estimasi eksperimental berbasis data terbuka.</p>
        <div><a href="#fitur">Fitur</a><a href="#edukasi">Edukasi</a><a href="#aksi">Aksi</a></div>
      </footer>
    </div>
  );
}
